import React, { useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  LayoutDashboard, 
  Image, 
  Building2, 
  FolderKanban, 
  Smartphone,
  Newspaper,
  Users,
  Briefcase,
  Mail,
  Settings,
  LogOut,
  Globe,
  ChevronLeft,
  ChevronRight,
  UserCog,
  FileText,
  Shield
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';

const AdminLayout = () => {
  const { user, logout, loading, isAuthenticated, isSuperAdmin } = useAuth();
  const { language, toggleLanguage, isRTL, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-raad-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-raad-rose border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: { ar: 'لوحة التحكم', en: 'Dashboard' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/sliders', icon: Image, label: { ar: 'الشرائح', en: 'Sliders' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/companies', icon: Building2, label: { ar: 'الشركات', en: 'Companies' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/projects', icon: FolderKanban, label: { ar: 'المشاريع', en: 'Projects' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/ehiaa', icon: FileText, label: { ar: 'مبادرة إحياء', en: 'Ehiaa Initiative' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/applications', icon: Smartphone, label: { ar: 'التطبيقات', en: 'Applications' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/news', icon: Newspaper, label: { ar: 'الأخبار', en: 'News' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/board', icon: Users, label: { ar: 'مجلس الإدارة', en: 'Board' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/jobs', icon: Briefcase, label: { ar: 'الوظائف', en: 'Jobs' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/job-applications', icon: FileText, label: { ar: 'طلبات التوظيف', en: 'Applications' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/contacts', icon: Mail, label: { ar: 'الرسائل', en: 'Messages' }, roles: ['super_admin', 'data_entry'] },
    { path: '/admin/admins', icon: UserCog, label: { ar: 'المسؤولين', en: 'Admins' }, roles: ['super_admin'] },
    { path: '/admin/settings', icon: Settings, label: { ar: 'الإعدادات', en: 'Settings' }, roles: ['super_admin', 'data_entry'] },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user?.role || 'data_entry')
  );

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="min-h-screen bg-raad-dark flex" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Sidebar */}
      <aside className="w-64 bg-raad-paper border-e border-white/10 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-3">
            <img 
              src="/assets/logo.png" 
              alt={t('رعد القابضة', 'Raad Holding')} 
              className="h-10 w-auto object-contain rounded-xl bg-white p-2"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-10 h-10 rounded-lg bg-gradient-raad items-center justify-center hidden">
              <span className="text-white font-bold text-xl">R</span>
            </div>
            <div>
              <span className="gradient-text font-bold text-lg block">
                {t('رعد القابضة', 'Raad Holding')}
              </span>
              <span className="text-gray-500 text-xs">
                {t('لوحة التحكم', 'Admin Panel')}
              </span>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all ${
                    isActive
                      ? 'bg-gradient-raad text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {t(item.label.ar, item.label.en)}
                  {isActive && <ChevronIcon className="w-4 h-4 ms-auto" />}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-raad-card flex items-center justify-center">
              <span className="text-raad-gold font-semibold">
                {user?.username?.[0]?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">
                {user?.name_ar || user?.name_en || user?.username || 'Admin'}
              </p>
              <div className="flex items-center gap-1">
                {isSuperAdmin && <Shield className="w-3 h-3 text-raad-gold" />}
                <p className="text-gray-500 text-xs">
                  {isSuperAdmin ? t('مسؤول رئيسي', 'Super Admin') : t('مدخل بيانات', 'Data Entry')}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="flex-1 text-gray-400 hover:text-white hover:bg-white/10"
            >
              <Globe className="w-4 h-4 me-2" />
              {language === 'ar' ? 'EN' : 'عربي'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="flex-1 text-gray-400 hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 me-2" />
              {t('خروج', 'Logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
